/**
 * Money.
 *
 * Every amount in this system is an INTEGER in the smallest unit of its currency
 * (agorot for ILS, cents for USD/EUR, fils for JOD). Floating point is never used
 * to hold or add money, because 0.1 + 0.2 !== 0.3 in every computer language.
 */

/** An integer number of minor units (agorot / cents / fils). */
export type Minor = number;

export class MoneyError extends Error {}

export function assertMinor(v: unknown, what = 'amount'): asserts v is Minor {
  if (typeof v !== 'number' || !Number.isInteger(v) || !Number.isFinite(v)) {
    throw new MoneyError(`${what} must be an integer number of minor units, got ${String(v)}`);
  }
}

/** Round half away from zero — the rule shoppers expect (2.5 -> 3, -2.5 -> -3). */
export function roundHalfUp(n: number): number {
  return n < 0 ? -Math.round(-n) : Math.round(n);
}

/** Multiply money by a plain quantity. Exact: quantities are whole. */
export function multiply(amount: Minor, qty: number): Minor {
  assertMinor(amount);
  if (!Number.isInteger(qty)) throw new MoneyError('quantity must be a whole number');
  return amount * qty;
}

/** Apply a percentage (e.g. 12.5 for 12.5%) and round to the nearest minor unit. */
export function percentOf(amount: Minor, percent: number): Minor {
  assertMinor(amount);
  if (!Number.isFinite(percent)) throw new MoneyError('percent must be a number');
  return roundHalfUp((amount * percent) / 100);
}

export function sum(amounts: Minor[]): Minor {
  let t = 0;
  for (const a of amounts) {
    assertMinor(a);
    t += a;
  }
  return t;
}

/** Never let a total go below zero (a discount bigger than the line, for example). */
export function clampAtZero(amount: Minor): Minor {
  assertMinor(amount);
  return amount < 0 ? 0 : amount;
}

/**
 * Split an amount into n parts that add back up EXACTLY to the original.
 * Used for spreading a whole-sale discount over the lines without losing an agora.
 */
export function allocate(amount: Minor, weights: number[]): Minor[] {
  assertMinor(amount);
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight <= 0) return weights.map(() => 0);
  const out = weights.map((w) => Math.floor((amount * w) / totalWeight));
  let remainder = amount - out.reduce((a, b) => a + b, 0);
  // Give the leftover agorot to the biggest weights first, one each.
  const order = weights.map((w, i) => ({ w, i })).sort((a, b) => b.w - a.w);
  let k = 0;
  while (remainder > 0 && order.length > 0) {
    out[order[k % order.length].i] += 1;
    remainder -= 1;
    k += 1;
  }
  return out;
}

/**
 * Cash rounding. Israeli cash totals are rounded to the nearest 0.10 ILS
 * because the 1 and 5 agorot coins no longer exist. Card is never rounded.
 */
export function roundCash(amountAgorot: Minor, stepAgorot = 10): Minor {
  assertMinor(amountAgorot);
  if (stepAgorot <= 1) return amountAgorot;
  return roundHalfUp(amountAgorot / stepAgorot) * stepAgorot;
}

/** Difference the rounding introduced; it has to be recorded so the books balance. */
export function cashRoundingDelta(amountAgorot: Minor, stepAgorot = 10): Minor {
  return roundCash(amountAgorot, stepAgorot) - amountAgorot;
}

/** 1050 -> "10.50". Formatting only; never feed the result back into a calculation. */
export function formatMinor(amount: Minor, decimals = 2): string {
  assertMinor(amount);
  const neg = amount < 0;
  const abs = Math.abs(amount);
  const d = 10 ** decimals;
  const whole = Math.floor(abs / d);
  const frac = abs % d;
  const s = decimals > 0 ? `${whole}.${String(frac).padStart(decimals, '0')}` : String(whole);
  return neg ? `-${s}` : s;
}

/** Arabic-Indic and Persian digits -> 0-9, so a cashier can type either. */
export function toWesternDigits(s: string): string {
  return s.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
}

/** "10.50" or 10.5 -> 1050. Used only at the edge, when a human types a price. */
export function parseMinor(input: string | number, decimals = 2): Minor {
  const s = toWesternDigits(String(input).trim())
    .replace(/[٬,\s]/g, '')
    .replace(/[٫،]/g, '.');
  if (s === '' || s === '-') return 0;
  if (!/^-?\d*(\.\d*)?$/.test(s)) throw new MoneyError(`cannot read "${input}" as an amount`);
  const neg = s.startsWith('-');
  const [w, f = ''] = s.replace('-', '').split('.');
  const frac = (f + '0'.repeat(decimals)).slice(0, decimals);
  const v = Number(w || '0') * 10 ** decimals + Number(frac || '0');
  return neg ? -v : v;
}
